import { useState, useCallback } from 'react';
import { toast } from 'sonner';

import { useUploadConsignmentAttachmentMutation, type AttachmentType } from '@/entities/consignments';

export interface AttachmentUploadFormData {
  file: File | null;
  attachmentType: AttachmentType | null;
  comment?: string;
}

interface UseAttachmentUploadParams {
  consignmentId: string;
  projectId?: number;
  siteId?: number;
  onSuccess?: () => void;
}

export function useAttachmentUpload({ consignmentId, projectId, siteId, onSuccess }: UseAttachmentUploadParams) {
  const [uploadAttachment, { isLoading }] = useUploadConsignmentAttachmentMutation();

  const [formData, setFormData] = useState<AttachmentUploadFormData>({
    file: null,
    attachmentType: null,
    comment: ''
  });

  const validateFile = useCallback((file: File): boolean => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];

    if (!allowedTypes.includes(file.type) && !file.type.startsWith('image/')) {
      toast.error('Invalid file type', {
        description: 'Please upload a PDF or image file'
      });
      return false;
    }

    if (file.size > 20 * 1024 * 1024) {
      toast.error('File too large', {
        description: 'File size must be less than 20MB'
      });
      return false;
    }

    return true;
  }, []);

  const handleFileSelect = useCallback(
    (files: FileList) => {
      if (files.length > 0) {
        const file = files[0];
        if (validateFile(file)) {
          setFormData((prev) => ({ ...prev, file }));
        }
      }
    },
    [validateFile]
  );

  const handleRemoveFile = useCallback(() => {
    setFormData((prev) => ({ ...prev, file: null }));
  }, []);

  const handleAttachmentTypeChange = useCallback((value: AttachmentType) => {
    setFormData((prev) => ({ ...prev, attachmentType: value }));
  }, []);

  const handleCommentChange = useCallback((value: string) => {
    setFormData((prev) => ({ ...prev, comment: value.slice(0, 256) }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      file: null,
      attachmentType: null,
      comment: ''
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!formData.file || !formData.attachmentType) {
      toast.error('Missing required fields', {
        description: 'Please select a file and attachment type'
      });
      return;
    }

    try {
      await uploadAttachment({
        id: consignmentId,
        projectId,
        siteId,
        file: formData.file,
        attachmentType: formData.attachmentType,
        comment: formData.comment
      }).unwrap();

      toast.success('Attachment uploaded successfully');

      onSuccess?.();
      resetForm();
    } catch (error) {
      toast.error('Upload failed', {
        description: 'Failed to upload attachment. Please try again.'
      });
    }
  }, [
    formData.file,
    formData.attachmentType,
    formData.comment,
    uploadAttachment,
    consignmentId,
    projectId,
    siteId,
    onSuccess,
    resetForm
  ]);

  return {
    formData,
    isLoading,
    handleFileSelect,
    handleRemoveFile,
    handleAttachmentTypeChange,
    handleCommentChange,
    handleSubmit,
    resetForm
  };
}
